import React, { memo, useEffect, useCallback, useState} from 'react';
import {isNumber} from 'utils/IsNumber.js';
import {toggleStep} from 'operations/actions.js';
import {setTrackerParameterToEdit} from 'actions/index.js';
import {getSteps} from './selector.js';
import Notes from 'midi/Notes.js';
import {connect} from 'react-redux';
import Colors from 'components/Colors.js';
const colors = new Colors();

const NUM_STEPS = 7;

const Tracker = ({
    track,
    pattern,
    currentStep,
    step,
    parameterToEdit,
    isSelectedTrack,
    cursorStep,
    setParameterToEdit}) => { 
        let [color, setColor] = useState(undefined);
        let [value, setValue] = useState(parameterToEdit && parameterToEdit.get());

        useEffect(() => {
            if (parameterToEdit) {
                setValue(parameterToEdit.get());
            }
        }, [setValue, parameterToEdit]);

    useEffect(() => {
        setColor(colors.getColorForTrack(track));
    }, [track, setColor]);

    useEffect(() => {
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [parameterToEdit, setValue, track]);

    const onKeyDown = useCallback((e) => {
        if (!parameterToEdit || parameterToEdit.track !== track) {
            return;
        }

        e.stopPropagation();

        let value  = parameterToEdit.get() ||
            track.trackData.parameters.find(x => x.name === parameterToEdit.name).get();

        if (isNumber(e.key)) {
            let number = parseInt(e.key);
            parameterToEdit.set(number);
            setValue(number);
        } else if (e.key >= 'a' && e.key <= 'g') {
            if (parameterToEdit.name === "Transpose") {
                let note = Notes.fromTranspose(parameterToEdit.get());
                note.note = e.key.toUpperCase();
                parameterToEdit.set(note.getTranspose());
                setValue(note.getTranspose());
            }
        } else if (e.key === "ArrowUp") {
            parameterToEdit.set(value + 1);
            setValue(value + 1);
        } else if (e.key === "ArrowDown") {
            parameterToEdit.set(value - 1);
            setValue(value - 1);
        }

        // lets you edit
    }, [parameterToEdit, setValue, track]);

    let length = pattern.length() / 2.0;
    let steps = getSteps(pattern, (step || currentStep) % length);
    return (
        <div className="tracker">
          {steps.map((data, i) =>
                     <TrackerStep
                       parameterToEdit={parameterToEdit}
                       setParameterToEdit={setParameterToEdit}
                       track={track}
                       value={value}
                       key={i}
                       color={color}
                       isCursor={isSelectedTrack && data.stepNumber === cursorStep}
                       isCurrent={data.stepNumber === currentStep % length}
                       data={data}/>)}
        </div>);
}

const TrackerStep = ({track, value, data, isCursor, isCurrent, color, parameterToEdit, setParameterToEdit}) => {
    let {stepData, stepNumber} = data;

    const onClick= useCallback((e) => {
        e.stopPropagation();
        track.sequencerEngine.operationManager.dispatch(
            toggleStep(track, stepNumber*2)); 
    }, [stepNumber, track]);

    const onClickParameter = useCallback((parameter) => {
        setParameterToEdit(parameter);
        track.sequencerEngine.select(
            track.trackNumber,
            stepNumber);
    }, [stepNumber, track]);

    let className = "tracker-step";
    if (isCurrent) {
        className += " current";
    }

    if (isCursor) {
        className += " cursor";
    }

    return (
        <div
          onClick={!stepData.on ? onClick : () => []}
          className={className}>
          <div
            onClick={onClick}
            className={
                stepNumber % 8 === 0 ? "bar step-number" :
                    stepNumber % 4 === 0 ? "half-bar step-number" : "step-number"}>
            {stepNumber + 1}</div>
          {stepData.on ?
           <div className="step-data">
             <div
               style={{color: color}}
               onClick={() => onClickParameter(stepData.transposeParameter)}
               className={parameterToEdit === stepData.transposeParameter ? "edit note-name" : "note-name"}>
               {Notes.fromTranspose(stepData.transpose).toString()}
             </div>
             <TrackerParameter
               v={value}
               value={stepData.duration}
               parameter={stepData.durationParameter}
               parameterToEdit={parameterToEdit}
               setParameterToEdit={onClickParameter}
             />
             <TrackerParameter
               v={value}
               value={stepData.chop}
               parameter={stepData.chopParameter}
               parameterToEdit={parameterToEdit}
               setParameterToEdit={onClickParameter}
             />
             <TrackerParameter
               v={value}
               value={stepData.slice}
               parameter={stepData.sliceParameter}
               parameterToEdit={parameterToEdit}
               setParameterToEdit={onClickParameter}
             />
           </div> :
           ''}
        </div>
    );
};

const TrackerParameter = ({value, parameter, setParameterToEdit, parameterToEdit}) => {
    return (
        <div
          onClick={() => setParameterToEdit(parameter)}
          className={parameter === parameterToEdit ? "edit tracker-parameter" : "tracker-parameter"}>
          {value}
        </div>
    );
};

const mapStateToProps = (state, props) => {
    let cursor = props.track.sequencerEngine.cursor;
    let parameterToEdit = state.mix.trackerParameterToEdit;
    return {
        steps: state.tracks.trackSteps[props.track.trackNumber],
        stepBuckets: props.pattern.stepBuckets,
        parameterToEdit,
        isSelectedTrack: cursor.track === props.track.trackNumber
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
      setParameterToEdit: (param) => dispatch(setTrackerParameterToEdit(param))
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Tracker);

